'use client';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { Flame, ShoppingCart, Plus, Minus, X, Calendar, Phone, MapPin, Clock, Check, Utensils, Home, Search, Menu, Smartphone, Share2, Info, Store, WifiOff, RefreshCw, MessagesSquare, LayoutGrid, List, ImageIcon, Grid3x3, AlignLeft, Square, SlidersHorizontal, ArrowDownUp, ImageOff, Mic, Wand2, UserRound } from 'lucide-react';
import { Input, Textarea, Modal, EmptyState } from '@components/ui';
import { RestaurantAssistant, type RestaurantAssistantAction } from './RestaurantAssistant';
import { OrderWizard } from './OrderWizard';
import { CartSuggestions } from './CartSuggestions';
import { DemoQuickAccess } from '@components/DemoQuickAccess';
import { CustomerAuthPanel, type PublicCustomer } from '@components/public/CustomerAuthPanel';
import { findCustomRule, buildCustomNotes } from '@/lib/menu-customization';
import { computeUpsellSuggestions, effectiveCategoryKind } from '@/lib/upsell-engine';
import type { CategoryKind, OrderWizardSettings, PublicMenuItem, UpsellRule } from '@/types/menu-sales';

interface MenuItem {
  id: string;
  category_id: string;
  name: string;
  description: string;
  price: number;
  image?: string | null;
}
interface MenuCategory {
  id: string;
  name: string;
  sort_order: number;
  image?: string | null;
  kind?: CategoryKind;
}
interface Promotion {
  id: string;
  name: string;
  description: string;
  banner_text: string;
  promo_code: string;
  is_featured: boolean;
}
interface EventItem {
  id: string;
  name: string;
  type: string;
  start_date: string;
  start_time: string;
  capacity: number;
  reserved_count: number;
  price_per_person: number;
  description: string;
}
interface TenantInfo {
  slug: string;
  name: string;
  tagline: string;
  logo: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  phone: string;
  address: string;
  city: string;
  hours: string;
  currency?: string;
}
interface BranchInfo {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hours?: string | null;
  notes?: string | null;
  slug?: string | null;
  source_url?: string | null;
  sort_order?: number | null;
  is_default?: boolean;
}

interface UserLocation {
  lat: number;
  lng: number;
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function hexToRgb(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r} ${g} ${b}`;
}

const DEFAULT_DISH_IMAGE = 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=720&q=80';
const PROMO_IMAGE = 'https://images.unsplash.com/photo-1532550907401-a500c9a57435?auto=format&fit=crop&w=900&q=80';
const DISH_IMAGE_RULES = [
  { keys: ['frango', 'galinha', 'asas', 'asinhas', 'churrasco'], src: 'https://images.unsplash.com/photo-1532550907401-a500c9a57435?auto=format&fit=crop&w=720&q=80' },
  { keys: ['bacalhau', 'dourada', 'robalo', 'salmao', 'peixe'], src: 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?auto=format&fit=crop&w=720&q=80' },
  { keys: ['sobremesa', 'pudim', 'mousse', 'arroz doce', 'leite creme', 'molotof'], src: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?auto=format&fit=crop&w=720&q=80' },
  { keys: ['bebida', 'vinho', 'cerveja', 'sumo', 'agua', 'cafe'], src: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?auto=format&fit=crop&w=720&q=80' },
  { keys: ['picanha', 'bife', 'prego', 'vitela', 'carne', 'grelhada'], src: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&w=720&q=80' },
  { keys: ['entrecosto', 'piano', 'porco', 'febras', 'secretos'], src: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?auto=format&fit=crop&w=720&q=80' },
  { keys: ['batata', 'arroz', 'feijao', 'salada', 'legumes', 'migas'], src: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=720&q=80' },
];

function resolveDishImage(item: MenuItem, categoryName?: string): string {
  if (item.image?.trim()) return item.image.trim();
  const haystack = `${item.name} ${item.description ?? ''} ${categoryName ?? ''}`.toLowerCase();
  return DISH_IMAGE_RULES.find((rule) => rule.keys.some((key) => haystack.includes(key)))?.src ?? DEFAULT_DISH_IMAGE;
}

function phoneHref(phone?: string | null): string {
  return String(phone || '').replace(/\s+/g, '');
}

function branchLabel(branch?: BranchInfo | null): string {
  if (!branch) return 'Escolher loja';
  return branch.city && !branch.name.toLowerCase().includes(branch.city.toLowerCase())
    ? `${branch.name}, ${branch.city}`
    : branch.name;
}

function branchAddress(branch?: BranchInfo | null): string {
  if (!branch) return '';
  return [branch.address, branch.zip_code, branch.city].filter(Boolean).join(', ');
}

function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

function distanceKm(from: UserLocation, branch: BranchInfo): number | null {
  if (typeof branch.latitude !== 'number' || typeof branch.longitude !== 'number') return null;
  const earthRadiusKm = 6371;
  const dLat = toRadians(branch.latitude - from.lat);
  const dLng = toRadians(branch.longitude - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(branch.latitude);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function TenantApp() {
  const reduce = useReducedMotion();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [branches, setBranches] = useState<BranchInfo[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [showBranchList, setShowBranchList] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [displayCount, setDisplayCount] = useState(10);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [cart, setCart] = useState<Array<{ lineId: string; id: string; name: string; price: number; quantity: number; image?: string; notes?: string }>>([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [orderSent, setOrderSent] = useState(false);
  const [orderQueued, setOrderQueued] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [customer, setCustomer] = useState({ name: '', phone: '', email: '', notes: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [productQty, setProductQty] = useState(1);
  const [customSelections, setCustomSelections] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState('home');
  const [isOnline, setIsOnline] = useState(true);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [draftReady, setDraftReady] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [menuView, setMenuView] = useState<'list' | 'grid' | 'gallery' | 'mosaic' | 'text' | 'cards'>('list');
  const [sortBy, setSortBy] = useState<'relevance' | 'price-asc' | 'price-desc' | 'name'>('relevance');
  const [priceFilter, setPriceFilter] = useState<'all' | 'under5' | '5to10' | '10to15' | 'over15'>('all');
  const [hasImageOnly, setHasImageOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [upsellRules, setUpsellRules] = useState<UpsellRule[]>([]);
  const [orderWizardSettings, setOrderWizardSettings] = useState<OrderWizardSettings>({ enabled: true, allow_skip: true });
  const [showOrderWizard, setShowOrderWizard] = useState(false);
  const [dismissedRuleIds, setDismissedRuleIds] = useState<Set<string>>(new Set());
  const [lastAddedItem, setLastAddedItem] = useState<MenuItem | null>(null);
  const [demoQuickAccess, setDemoQuickAccess] = useState(false);
  const [crmEnabled, setCrmEnabled] = useState(false);
  const [loyaltyCustomer, setLoyaltyCustomer] = useState<PublicCustomer | null>(null);
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const accountSectionRef = useRef<HTMLDivElement>(null);
  const menuSectionRef = useRef<HTMLElement>(null);
  const offersSectionRef = useRef<HTMLElement>(null);
  const branchSectionRef = useRef<HTMLElement>(null);

  // Dev fallback: if ?tenant=<slug> is present, pass it to public APIs so they
  // can resolve the tenant on localhost (where no subdomain is available).
  const tenantParam = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('tenant')
    : null;
  const apiQuery = tenantParam ? `?tenant=${tenantParam}` : '';
  const draftKey = `restaurant-order:${tenantParam || 'current-tenant'}`;
  const branchKey = `${draftKey}:branch`;
  const viewKey = `${draftKey}:menu-view`;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`/api/public/menu${apiQuery}`);
      if (!res.ok) throw new Error('Nao foi possivel carregar a ementa.');
      const data = await res.json();
      setTenant(data.tenant);
      setRestaurant(data.restaurant);
      setBranches((data.branches ?? []).map((branch: any) => ({
        ...branch,
        latitude: branch.latitude === null || branch.latitude === undefined ? null : Number(branch.latitude),
        longitude: branch.longitude === null || branch.longitude === undefined ? null : Number(branch.longitude),
        sort_order: branch.sort_order === null || branch.sort_order === undefined ? null : Number(branch.sort_order),
        is_default: branch.is_default === true,
      })));
      setCategories((data.categories ?? []).map((c: any) => ({
        ...c,
        kind: c.kind || 'other',
      })));
      setItems((data.items ?? []).map((i: any) => ({
        ...i,
        description: i.description ?? '',
        image: i.image ?? null,
        price: parseFloat(i.price),
      })));
      setUpsellRules(data.upsell_rules ?? []);
      setOrderWizardSettings(data.order_wizard ?? { enabled: true, allow_skip: true });
      setPromotions(data.promotions ?? []);
      setEvents(data.events ?? []);
      setDemoQuickAccess(Boolean(data.features?.demo_quick_access));
      setCrmEnabled(Boolean(data.features?.crm));
    } catch {
      setLoadError('Não foi possível carregar a ementa. Verifica a ligação e tenta novamente.');
    } finally {
      setLoading(false);
    }
  }, [apiQuery]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (branches.length === 0) return;

    setSelectedBranchId((current) => {
      if (current && branches.some((branch) => branch.id === current)) return current;
      const saved = localStorage.getItem(branchKey);
      if (saved && branches.some((branch) => branch.id === saved)) return saved;
      return branches.find((branch) => branch.is_default)?.id ?? branches[0].id;
    });
  }, [branches, branchKey]);

  useEffect(() => {
    if (!selectedBranchId) return;
    localStorage.setItem(branchKey, selectedBranchId);
  }, [selectedBranchId, branchKey]);

  useEffect(() => {
    setIsOnline(navigator.onLine);
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    const onInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('beforeinstallprompt', onInstallPrompt);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('beforeinstallprompt', onInstallPrompt);
    };
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const draft = JSON.parse(saved);
        setCart(Array.isArray(draft.cart) ? draft.cart : []);
        setCustomer((current) => ({ ...current, ...(draft.customer ?? {}) }));
      }
    } catch {
      localStorage.removeItem(draftKey);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey]);

  useEffect(() => {
    if (!draftReady) return;
    localStorage.setItem(draftKey, JSON.stringify({ cart, customer }));
  }, [cart, customer, draftKey, draftReady]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(viewKey);
      if (
        saved === 'list' ||
        saved === 'cards' ||
        saved === 'grid' ||
        saved === 'gallery' ||
        saved === 'mosaic' ||
        saved === 'text'
      ) {
        setMenuView(saved);
      }
    } catch { /* ignore */ }
  }, [viewKey]);

  useEffect(() => {
    try { localStorage.setItem(viewKey, menuView); } catch { /* ignore */ }
  }, [menuView, viewKey]);

  useEffect(() => {
    if (!tenant) return;
    const syncHead = () => {
      document.querySelectorAll<HTMLLinkElement>('link[rel="manifest"]').forEach((link) => {
        const nextManifest = `/api/public/manifest${apiQuery}`;
        if (!link.href.endsWith(nextManifest)) link.href = nextManifest;
      });
      document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((meta) => {
        if (meta.content !== tenant.primaryColor) meta.content = tenant.primaryColor;
      });
      if (document.title !== `${tenant.name} - Encomendas`) {
        document.title = `${tenant.name} - Encomendas`;
      }
    };
    syncHead();
    const observer = new MutationObserver(syncHead);
    observer.observe(document.head, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [tenant, apiQuery]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`${draftKey}:dismissed-upsell`);
      if (raw) setDismissedRuleIds(new Set(JSON.parse(raw)));
    } catch { /* ignore */ }
  }, [draftKey]);

  const dismissUpsellRule = useCallback((ruleId: string) => {
    setDismissedRuleIds((prev) => {
      const next = new Set(prev);
      next.add(ruleId);
      try {
        sessionStorage.setItem(`${draftKey}:dismissed-upsell`, JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  }, [draftKey]);

  const salesCategories = useMemo(
    () => categories.map((c) => ({
      id: c.id,
      name: c.name,
      kind: effectiveCategoryKind({
        id: c.id,
        name: c.name,
        kind: (c.kind || 'other') as CategoryKind,
      }),
    })),
    [categories],
  );

  const cartItemIds = useMemo(() => cart.map((c) => c.id), [cart]);

  const cartSuggestions = useMemo(() => computeUpsellSuggestions({
    rules: upsellRules,
    cartItemIds,
    items: items as PublicMenuItem[],
    categories: salesCategories,
    context: 'cart',
    triggerItem: lastAddedItem as PublicMenuItem | undefined,
    dismissedRuleIds,
    limit: 2,
  }), [upsellRules, cartItemIds, items, salesCategories, lastAddedItem, dismissedRuleIds]);

  const postAddSuggestions = useMemo(() => {
    if (!lastAddedItem) return [];
    return computeUpsellSuggestions({
      rules: upsellRules,
      cartItemIds,
      items: items as PublicMenuItem[],
      categories: salesCategories,
      context: 'cart',
      triggerItem: lastAddedItem as PublicMenuItem,
      dismissedRuleIds,
      limit: 1,
    });
  }, [lastAddedItem, upsellRules, cartItemIds, items, salesCategories, dismissedRuleIds]);

  const categoryById = useMemo(() => new Map(categories.map((cat) => [cat.id, cat])), [categories]);
  const activeCategoryName = activeCategory === 'all' ? 'Ementa' : categoryById.get(activeCategory)?.name ?? 'Ementa';
  // Regra de personalizacao (molho, ponto, etc.) aplicavel ao produto aberto.
  const pendingRule = useMemo(
    () => selectedProduct ? findCustomRule(categoryById.get(selectedProduct.category_id)?.name, selectedProduct.name) : null,
    [selectedProduct, categoryById],
  );
  const selectedBranch = useMemo(
    () => branches.find((branch) => branch.id === selectedBranchId) ?? branches.find((branch) => branch.is_default) ?? branches[0] ?? null,
    [branches, selectedBranchId],
  );
  const selectedBranchDistance = selectedBranch && userLocation ? distanceKm(userLocation, selectedBranch) : null;
  const orderedBranches = useMemo(() => {
    const withDistance = branches.map((branch) => ({
      branch,
      distance: userLocation ? distanceKm(userLocation, branch) : null,
    }));

    return withDistance.sort((a, b) => {
      if (a.distance !== null && b.distance !== null) return a.distance - b.distance;
      if (a.distance !== null) return -1;
      if (b.distance !== null) return 1;
      return (a.branch.sort_order ?? 999) - (b.branch.sort_order ?? 999);
    });
  }, [branches, userLocation]);

  const filteredItems = useMemo(() => {
    let result = items.filter((i) => {
      if (activeCategory !== 'all' && i.category_id !== activeCategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (!i.name.toLowerCase().includes(q) && !(i.description ?? '').toLowerCase().includes(q)) return false;
      }
      if (hasImageOnly && !i.image) return false;
      if (priceFilter !== 'all') {
        const p = Number(i.price);
        if (priceFilter === 'under5' && p >= 5) return false;
        if (priceFilter === '5to10' && (p < 5 || p > 10)) return false;
        if (priceFilter === '10to15' && (p < 10 || p > 15)) return false;
        if (priceFilter === 'over15' && p < 15) return false;
      }
      return true;
    });
    if (sortBy === 'price-asc') result = [...result].sort((a, b) => a.price - b.price);
    else if (sortBy === 'price-desc') result = [...result].sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt'));
    return result;
  }, [items, activeCategory, searchQuery, hasImageOnly, priceFilter, sortBy]);

  // Reset pagination when filters change
  useEffect(() => {
    setDisplayCount(10);
  }, [activeCategory, searchQuery, hasImageOnly, priceFilter, sortBy]);

  const visibleItems = filteredItems.slice(0, displayCount);
  const hasMore = filteredItems.length > displayCount;

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const addToCart = (item: MenuItem, notes?: string) => {
    const lineId = notes ? `${item.id}::${notes}` : item.id;
    setCart((prev) => {
      const existing = prev.find((i) => i.lineId === lineId);
      if (existing) return prev.map((i) => (i.lineId === lineId ? { ...i, quantity: i.quantity + 1 } : i));
      return [...prev, {
        lineId,
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        image: resolveDishImage(item, categoryById.get(item.category_id)?.name),
        notes,
      }];
    });
    setLastAddedItem(item);
  };

  const updateQty = (lineId: string, delta: number) => {
    setCart((prev) => prev
      .map((i) => (i.lineId === lineId ? { ...i, quantity: i.quantity + delta } : i))
      .filter((i) => i.quantity > 0));
  };

  const removeFromCart = (lineId: string) => setCart((prev) => prev.filter((i) => i.lineId !== lineId));

  const startVoice = useCallback(async () => {
    if (voiceActive) {
      window.VoiceMode?.end();
      setVoiceActive(false);
      return;
    }
    if (!window.VoiceMode) {
      // Load voice.js on demand
      const script = document.createElement('script');
      script.src = '/voice/voice.js';
      await new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load voice.js'));
        document.head.appendChild(script);
      });
    }
    if (!window.VoiceMode) return;
    if (!tenant) return;
    setVoiceActive(true);
    await window.VoiceMode.start(tenant.slug, { mode: 'realtime', branch_id: selectedBranch?.id });
    setVoiceActive(false);
  }, [voiceActive, tenant, selectedBranch?.id]);

  const applyAssistantAction = (action: RestaurantAssistantAction) => {
    if (action.type === 'add_item') {
      const item = items.find((candidate) => candidate.id === action.item_id);
      if (!item) return;
      const quantity = Math.min(Math.max(Number(action.quantity || 1), 1), 12);
      for (let index = 0; index < quantity; index += 1) addToCart(item);
      return;
    }

    if (action.type === 'search') {
      setSearchQuery(action.query);
      setActiveTab('search');
      setAssistantOpen(false);
      window.requestAnimationFrame(() => {
        menuSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    if (action.type === 'open_cart') {
      setActiveTab('cart');
      setAssistantOpen(false);
      setShowCart(true);
      return;
    }

    if (action.type === 'open_checkout') {
      if (cart.length > 0) {
        setAssistantOpen(false);
        setShowCart(false);
        setShowCheckout(true);
      }
      return;
    }

    if (action.type === 'set_customer') {
      setCustomer((current) => ({
        ...current,
        name: action.name ?? current.name,
        phone: action.phone ?? current.phone,
        email: action.email ?? current.email,
        notes: action.notes ?? current.notes,
      }));
      return;
    }

    if (action.type === 'call_restaurant') {
      setAssistantOpen(false);
      window.location.href = `tel:${phoneHref(selectedBranch?.phone || tenant?.phone)}`;
    }
  };

  const focusSearch = () => {
    setActiveTab('search');
    menuSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => searchInputRef.current?.focus(), 250);
  };

  const chooseBranch = (branchId: string) => {
    setSelectedBranchId(branchId);
    setShowBranchList(false);
  };

  const useCurrentLocation = () => {
    setLocationError('');
    if (!navigator.geolocation) {
      setLocationError('Localização indisponível neste dispositivo.');
      return;
    }

    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(nextLocation);
        setLocating(false);

        const nearest = branches
          .map((branch) => ({ branch, distance: distanceKm(nextLocation, branch) }))
          .filter((entry): entry is { branch: BranchInfo; distance: number } => entry.distance !== null)
          .sort((a, b) => a.distance - b.distance)[0];
        if (nearest) setSelectedBranchId(nearest.branch.id);
      },
      () => {
        setLocating(false);
        setLocationError('Não foi possível obter a localização.');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  };

  const submitOrder = async () => {
    if (!customer.name || !customer.phone) return;
    setSubmittingOrder(true);
    setCheckoutError('');
    try {
      const res = await fetch(`/api/public/orders${apiQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart,
          customer,
          type: 'online',
          notes: customer.notes,
          branch_id: selectedBranch?.id ?? null,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json().catch(() => ({}));
      setOrderQueued(res.status === 202 || data.queued === true);
      setOrderSent(true);
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
      setShowOrderWizard(false);
      localStorage.removeItem(draftKey);
    } catch {
      setCheckoutError('Nao foi possivel enviar a encomenda. Mantivemos o carrinho para tentares novamente.');
    } finally {
      setSubmittingOrder(false);
    }
  };

  const installApp = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  const shareApp = async () => {
    setShowMenu(false);
    const shareData = {
      title: tenant?.name ?? 'Okai',
      text: tenant?.tagline ?? '',
      url: typeof window !== 'undefined' ? window.location.href : '',
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      try { await navigator.clipboard.writeText(shareData.url); } catch { /* ignore */ }
    }
  };

  const scrollToBranches = () => {
    setShowMenu(false);
    setShowBranchList(true);
    branchSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openAbout = () => {
    setShowMenu(false);
    setShowAbout(true);
  };

  const tenantStyle = tenant ? ({
    '--brand-rgb': hexToRgb(tenant.primaryColor),
    '--brand-2-rgb': hexToRgb(tenant.secondaryColor),
    '--brand-accent-rgb': hexToRgb(tenant.accentColor),
  } as React.CSSProperties) : {};
  const formatPrice = useCallback((value: number) => new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: tenant?.currency || 'EUR',
  }).format(Number(value) || 0), [tenant?.currency]);

  useEffect(() => {
    if (!loyaltyCustomer) return;
    setCustomer((current) => ({
      ...current,
      name: current.name || loyaltyCustomer.name || '',
      phone: current.phone || loyaltyCustomer.phone || '',
      email: current.email || loyaltyCustomer.email || '',
    }));
  }, [loyaltyCustomer]);

  if (loading) {
    return (
      <div className="yummer-container px-4 pt-24" style={tenantStyle}>
        <div className="space-y-4" aria-label="A carregar ementa">
          <div className="h-14 animate-pulse rounded-xl bg-surface-2" />
          <div className="h-24 animate-pulse rounded-xl bg-surface-2" />
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="h-52 animate-pulse rounded-xl bg-surface-2" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="yummer-container flex min-h-[100dvh] items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <WifiOff className="mx-auto h-10 w-10 text-ink-500" />
          <h1 className="mt-4 text-xl font-bold text-ink-900">Ementa indisponível</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-500">{loadError}</p>
          <button onClick={load} className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-ink-900 px-5 text-sm font-semibold text-white">
            <RefreshCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="yummer-container flex items-center justify-center">
        <EmptyState
          icon={<Flame className="w-12 h-12" />}
          title="Restaurante não encontrado"
          description="Este domínio não corresponde a nenhum restaurante ativo. Se é o proprietário, acede a /admin ou /super-admin."
        />
      </div>
    );
  }

  return (
    <div className="yummer-container" style={tenantStyle}>
      <header className="yummer-header">
        <div className="header-logo" aria-label={tenant.name}>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={tenant.logo} alt={tenant.name} className="header-logo-img" />
        </div>
        <h1 className="yummer-header-title">{tenant.name}</h1>
        <div className="header-right-group">
          {crmEnabled && (
            <button
              type="button"
              onClick={() => {
                setAccountPanelOpen(true);
                setShowMenu(false);
                requestAnimationFrame(() => {
                  accountSectionRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
                });
              }}
              className="header-icon-button relative"
              aria-label={loyaltyCustomer ? `Conta de ${loyaltyCustomer.name}` : 'Entrar ou criar conta'}
            >
              <UserRound className="w-6 h-6 text-ink-700" />
              {loyaltyCustomer && (
                <span className="cart-badge" aria-hidden="true">
                  {Math.min(99, loyaltyCustomer.points_balance ?? 0)}
                </span>
              )}
            </button>
          )}
          <button onClick={() => setShowMenu((v) => !v)} className="header-icon-button" aria-label="Menu" aria-expanded={showMenu}>
            <Menu className="w-6 h-6 text-ink-700" />
          </button>
          <button onClick={() => setShowCart(true)} className="header-icon-button relative" aria-label={`Carrinho, ${cartCount} itens`}>
            <ShoppingCart className="w-6 h-6 text-ink-700" />
            {cartCount > 0 && (
              <motion.span
                initial={reduce ? false : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                className="cart-badge"
              >
                {cartCount}
              </motion.span>
            )}
          </button>
        </div>

        {showMenu && (
          <>
            <div className="menu-backdrop" onClick={() => setShowMenu(false)} />
            <div className="header-menu" role="menu">
              <div className="header-menu-contact">
                <a href={`tel:${phoneHref(tenant.phone)}`} className="header-menu-contact-row">
                  <Phone className="h-4 w-4 text-brand" />
                  <span>{tenant.phone}</span>
                </a>
                <div className="header-menu-contact-row">
                  <MapPin className="h-4 w-4 text-brand" />
                  <span>{[tenant.address, tenant.city].filter(Boolean).join(', ')}</span>
                </div>
                <div className="header-menu-contact-row">
                  <Clock className="h-4 w-4 text-brand" />
                  <span>{tenant.hours}</span>
                </div>
              </div>
              <div className="header-menu-divider" />
              {crmEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMenu(false);
                    setAccountPanelOpen(true);
                    requestAnimationFrame(() => {
                      accountSectionRef.current?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
                    });
                  }}
                  className="header-menu-item"
                  role="menuitem"
                >
                  <UserRound className="h-4 w-4" />
                  <span>
                    {loyaltyCustomer
                      ? `A minha conta · ${loyaltyCustomer.points_balance ?? 0} pts`
                      : 'Entrar / Criar conta'}
                  </span>
                </button>
              )}
              {branches.length > 1 && (
                <button onClick={scrollToBranches} className="header-menu-item" role="menuitem">
                  <Store className="h-4 w-4" />
                  <span>Trocar loja</span>
                </button>
              )}
              <button onClick={shareApp} className="header-menu-item" role="menuitem">
                <Share2 className="h-4 w-4" />
                <span>Partilhar app</span>
              </button>
              <button onClick={openAbout} className="header-menu-item" role="menuitem">
                <Info className="h-4 w-4" />
                <span>Sobre o restaurante</span>
              </button>
              {installPrompt && (
                <button onClick={() => { setShowMenu(false); installApp(); }} className="header-menu-item" role="menuitem">
                  <Smartphone className="h-4 w-4" />
                  <span>Instalar app</span>
                </button>
              )}
            </div>
          </>
        )}
      </header>

      {!isOnline && (
        <div className="connection-banner" role="status">
          <WifiOff className="h-4 w-4" />
          Sem ligação. Podes continuar a preparar o pedido.
        </div>
      )}

      <main className={`yummer-main ${cartCount > 0 ? 'has-cart-cta' : ''} ${!isOnline ? 'is-offline' : ''}`}>
        {crmEnabled && (
          <div ref={accountSectionRef} className="mb-4 scroll-mt-[72px]">
            <CustomerAuthPanel
              enabled={crmEnabled}
              apiQuery={apiQuery}
              open={accountPanelOpen}
              onOpenChange={setAccountPanelOpen}
              onAuthenticated={setLoyaltyCustomer}
            />
          </div>
        )}
        <motion.section
          ref={branchSectionRef}
          initial={reduce ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="order-intro"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-brand">Pedido online</p>
              <h2 className="mt-1 font-display text-xl font-bold leading-tight text-ink-900">O que te apetece hoje?</h2>
              <p className="mt-1 line-clamp-2 text-sm text-ink-500">{tenant.tagline || restaurant?.description || 'Escolhe os teus favoritos e envia o pedido em poucos toques.'}</p>
            </div>
            <div className="order-intro-mark" aria-hidden="true">
              <Flame className="h-6 w-6" />
            </div>
          </div>
          {orderWizardSettings.enabled && (
            <button
              type="button"
              onClick={() => setShowOrderWizard(true)}
              className="order-wizard-cta mt-4 w-full"
            >
              <Wand2 className="w-5 h-5" />
              <span>
                <strong>Pedido guiado</strong>
                <small>Prato, acompanhamentos, bebidas e sobremesas passo a passo</small>
              </span>
            </button>
          )}
          <div className="mt-4 relative">
            <Input
              ref={searchInputRef}
              type="search"
              placeholder="Procurar na ementa (nome ou descrição)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
              className="h-12 rounded-xl border-surface-3 bg-surface-0 text-ink-900 placeholder:text-ink-500 pr-10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-700 transition-colors"
                aria-label="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {branches.length > 1 && (
            <div className="branch-selector">
              <div className="branch-selector-top">
                <button type="button" className="branch-current" onClick={() => setShowBranchList((value) => !value)}>
                  <span className="branch-current-icon"><MapPin className="h-4 w-4" /></span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="branch-current-label">Loja selecionada</span>
                    <span className="branch-current-name">{branchLabel(selectedBranch)}</span>
                  </span>
                </button>
                <button type="button" className="branch-location-button" onClick={useCurrentLocation} disabled={locating || branches.length === 0}>
                  {locating ? 'A localizar' : 'Mais perto'}
                </button>
              </div>

              {selectedBranch && (
                <div className="branch-current-meta">
                  <span><Clock className="h-3.5 w-3.5 text-brand" /> {selectedBranch.hours || tenant.hours}</span>
                  <span><Phone className="h-3.5 w-3.5 text-brand" /> {selectedBranch.phone || tenant.phone}</span>
                  {selectedBranchDistance !== null && (
                    <span>{selectedBranchDistance.toFixed(1)} km</span>
                  )}
                </div>
              )}
              {selectedBranch && (
                <p className="branch-current-address">{branchAddress(selectedBranch)}</p>
              )}
              {locationError && <p className="branch-error">{locationError}</p>}

              {showBranchList && (
                <div className="branch-list" role="listbox" aria-label="Escolher loja Rei dos Frangos">
                  {orderedBranches.map(({ branch, distance }) => (
                    <button
                      type="button"
                      key={branch.id}
                      className={`branch-option ${branch.id === selectedBranch?.id ? 'is-selected' : ''}`}
                      onClick={() => chooseBranch(branch.id)}
                    >
                      <span className="branch-option-main">
                        <span className="branch-option-name">{branchLabel(branch)}</span>
                        <span className="branch-option-address">{branchAddress(branch)}</span>
                      </span>
                      <span className="branch-option-side">
                        {distance !== null ? `${distance.toFixed(1)} km` : branch.hours || tenant.hours}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.section>

        {/* Featured offers */}
        {promotions.length > 0 && (
          <motion.section
            ref={offersSectionRef}
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10"
          >
            <div className="block-heading">
              <h2 className="block-heading-title">Em destaque</h2>
              <span className="block-heading-link">Ver todas</span>
            </div>
            <div className="h-scroll">
              {promotions.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={reduce ? false : { opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  className="offer-card h-scroll-item"
                  style={{ backgroundImage: `url(${PROMO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                >
                  <div className="offer-card-overlay">
                    <div>
                      <span className="offer-card-title">{p.banner_text || p.name}</span>
                      {p.promo_code && (
                        <span className="inline-block mt-1.5 bg-white/20 backdrop-blur-sm px-2.5 py-0.5 rounded-md font-mono text-[10px] text-white">
                          {p.promo_code}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Category chips - Foodia-style horizontal filters */}
        <section className="mb-6">
          <div className="h-scroll">
            <button
              onClick={() => setActiveCategory('all')}
              className={`h-scroll-item min-h-11 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                activeCategory === 'all'
                  ? 'bg-brand text-brand-contrast shadow-sm'
                  : 'bg-surface-0 text-ink-700 border border-surface-3 hover:border-ink-300'
              }`}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`h-scroll-item min-h-11 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeCategory === cat.id
                    ? 'bg-brand text-brand-contrast shadow-sm'
                    : 'bg-surface-0 text-ink-700 border border-surface-3 hover:border-ink-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </section>

        {/* Menu - block heading */}
        <section ref={menuSectionRef}>
          <div className="block-heading menu-heading">
            <h2 className="block-heading-title">{searchQuery.trim() ? 'Resultados' : activeCategoryName}</h2>
            <div className="menu-heading-actions">
              <span className="block-heading-link whitespace-nowrap">{filteredItems.length} itens</span>
              <div className="menu-view-toggle" role="group" aria-label="Modo de visualização">
                <button
                  className={`menu-view-btn ${menuView === 'list' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('list')}
                  aria-label="Vista em lista"
                  aria-pressed={menuView === 'list'}
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  className={`menu-view-btn ${menuView === 'cards' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('cards')}
                  aria-label="Vista em cartões"
                  aria-pressed={menuView === 'cards'}
                >
                  <Square className="w-4 h-4" />
                </button>
                <button
                  className={`menu-view-btn ${menuView === 'grid' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('grid')}
                  aria-label="Vista em grelha"
                  aria-pressed={menuView === 'grid'}
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  className={`menu-view-btn menu-view-advanced ${menuView === 'gallery' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('gallery')}
                  aria-label="Vista em galeria"
                  aria-pressed={menuView === 'gallery'}
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
                <button
                  className={`menu-view-btn menu-view-advanced ${menuView === 'mosaic' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('mosaic')}
                  aria-label="Vista em mosaico"
                  aria-pressed={menuView === 'mosaic'}
                >
                  <Grid3x3 className="w-4 h-4" />
                </button>
                <button
                  className={`menu-view-btn menu-view-advanced ${menuView === 'text' ? 'is-active' : ''}`}
                  onClick={() => setMenuView('text')}
                  aria-label="Vista em texto"
                  aria-pressed={menuView === 'text'}
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
              </div>
              <button
                type="button"
                className={`menu-filter-btn ${(showFilters || sortBy !== 'relevance' || priceFilter !== 'all' || hasImageOnly) ? 'is-active' : ''}`}
                onClick={() => setShowFilters((v) => !v)}
                aria-label="Filtros e ordenação"
                aria-expanded={showFilters}
              >
                <SlidersHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filter bar */}
          {showFilters && (
            <div className="menu-filter-bar">
              <div className="menu-filter-row">
                <span className="menu-filter-label">
                  <ArrowDownUp className="w-3.5 h-3.5" /> Ordenar
                </span>
                <div className="menu-filter-chips">
                  {([
                    ['relevance', 'Relevância'],
                    ['price-asc', 'Preço ↑'],
                    ['price-desc', 'Preço ↓'],
                    ['name', 'A-Z'],
                  ] as const).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      className={`menu-filter-chip ${sortBy === val ? 'is-active' : ''}`}
                      onClick={() => setSortBy(val)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="menu-filter-row">
                <span className="menu-filter-label">Preço</span>
                <div className="menu-filter-chips">
                  {([
                    ['all', 'Todos'],
                    ['under5', 'Até 5€'],
                    ['5to10', '5-10€'],
                    ['10to15', '10-15€'],
                    ['over15', '15€+'],
                  ] as const).map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      className={`menu-filter-chip ${priceFilter === val ? 'is-active' : ''}`}
                      onClick={() => setPriceFilter(val)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="menu-filter-row">
                <button
                  type="button"
                  className={`menu-filter-chip ${hasImageOnly ? 'is-active' : ''}`}
                  onClick={() => setHasImageOnly((v) => !v)}
                  aria-pressed={hasImageOnly}
                >
                  <ImageOff className="w-3.5 h-3.5" /> Só com imagem
                </button>
                {(sortBy !== 'relevance' || priceFilter !== 'all' || hasImageOnly) && (
                  <button
                    type="button"
                    className="menu-filter-clear"
                    onClick={() => { setSortBy('relevance'); setPriceFilter('all'); setHasImageOnly(false); }}
                  >
                    Limpar filtros
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Items */}
          {filteredItems.length === 0 ? (
            <EmptyState
              icon={searchQuery.trim() ? <Search className="w-12 h-12" /> : <Utensils className="w-12 h-12" />}
              title={searchQuery.trim() ? 'Sem resultados' : 'Sem itens nesta categoria'}
              description={searchQuery.trim() ? `Nada encontrado para "${searchQuery}". Tenta outra palavra.` : 'Tenta outra categoria ou volta mais tarde.'}
            />
          ) : (
            <>
            <div className={`menu-view menu-view-${menuView}`}>
              {visibleItems.map((item, i) => {
                const inCart = cart.find((c) => c.id === item.id);
                const qty = inCart ? inCart.quantity : 0;
                const imageSrc = resolveDishImage(item, categoryById.get(item.category_id)?.name);
                const openProduct = () => { setSelectedProduct(item); setProductQty(1); setCustomSelections({}); };
                const addBtn = (
                  <motion.button
                    whileTap={reduce ? undefined : { scale: 0.85 }}
                    onClick={(e) => { e.stopPropagation(); addToCart(item); }}
                    className="yummer-add-btn"
                    style={qty > 0 ? { backgroundColor: 'rgb(var(--brand-rgb))' } : { backgroundColor: 'rgb(var(--surface-1))' }}
                    aria-label={`Adicionar ${item.name}`}
                  >
                    {qty > 0 ? (
                      <motion.span
                        key={qty}
                        initial={reduce ? false : { scale: 0.5 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                        className="text-sm font-bold text-white"
                      >
                        {qty}
                      </motion.span>
                    ) : (
                      <Plus className="w-4 h-4 text-brand" />
                    )}
                  </motion.button>
                );
                return (
                  <motion.div
                    key={item.id}
                    initial={reduce ? false : { opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4), ease: [0.16, 1, 0.3, 1] }}
                    className="yummer-dish-card"
                    onClick={openProduct}
                  >
                    {menuView !== 'text' && (
                      <div className="yummer-dish-img bg-surface-2 overflow-hidden">
                        <img
                          src={imageSrc}
                          alt={item.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(event) => { event.currentTarget.src = DEFAULT_DISH_IMAGE; }}
                        />
                      </div>
                    )}
                    <div className="yummer-dish-body">
                      <h3 className="yummer-dish-name">
                        <button
                          className="min-h-11 -mx-2 flex w-[calc(100%+1rem)] items-center px-2 text-left hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                          onClick={(event) => { event.stopPropagation(); openProduct(); }}
                        >
                          {item.name}
                        </button>
                      </h3>
                      {menuView !== 'text' && (
                        <p className="text-xs text-ink-500 line-clamp-2 mb-2.5">{item.description}</p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="yummer-dish-price">{formatPrice(Number(item.price))}</span>
                        {addBtn}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            {hasMore && (
              <div className="flex justify-center mt-6 mb-4">
                <button
                  onClick={() => setDisplayCount((c) => c + 10)}
                  className="yummer-load-more"
                >
                  Ver mais ({filteredItems.length - displayCount} restantes)
                </button>
              </div>
            )}
            </>
          )}
        </section>

        {/* Events */}
        {events.length > 0 && (
          <section className="mt-8 mb-8">
            <div className="block-heading">
              <h2 className="block-heading-title">Próximos eventos</h2>
            </div>
            <div className="space-y-3">
              {events.map((ev, i) => (
                <motion.div
                  key={ev.id}
                  initial={reduce ? false : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.4, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="fk-card p-4"
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-ink-900">{ev.name}</h3>
                      <p className="text-sm text-ink-500 mt-0.5">{ev.description}</p>
                    </div>
                    <span className="text-lg font-bold text-brand flex-shrink-0">{formatPrice(Number(ev.price_per_person))}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-3 text-sm text-ink-700">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-ink-500" /> {ev.start_date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-ink-500" /> {ev.start_time}</span>
                    <span className="text-ink-500">{ev.reserved_count}/{ev.capacity}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}
      </main>

      {cartCount > 0 && (
          <button onClick={() => setShowCart(true)} className="yummer-cart-cta" aria-label={`Ver carrinho com ${cartCount} itens, total ${formatPrice(cartTotal)}`}>
          <span className="cart-cta-count">{cartCount}</span>
          <span className="flex-1 text-left">Ver carrinho</span>
            <span>{formatPrice(cartTotal)}</span>
        </button>
      )}

      {/* ===== Floating bottom tab bar ===== */}
      <div className="yummer-tabbar">
        <div className="yummer-tabbar-inner">
          <button className={`yummer-tab ${activeTab === 'home' ? 'yummer-tab-active' : ''}`} onClick={() => { setActiveTab('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Home className="w-5 h-5" />
            <span className="yummer-tab-label">Inicio</span>
          </button>
          <button className={`yummer-tab ${activeTab === 'search' ? 'yummer-tab-active' : ''}`} onClick={focusSearch}>
            <Search className="w-5 h-5" />
            <span className="yummer-tab-label">Procurar</span>
          </button>
          <button className={`yummer-tab ${assistantOpen ? 'yummer-tab-active' : ''}`} onClick={() => { setActiveTab('assistant'); setAssistantOpen(true); }}>
            <MessagesSquare className="w-5 h-5" />
            <span className="yummer-tab-label">Assistente</span>
          </button>
          <button className={`yummer-tab ${voiceActive ? 'yummer-tab-active' : ''}`} onClick={startVoice} aria-label="Modo voz">
            <Mic className="w-5 h-5" />
            <span className="yummer-tab-label">Voz</span>
          </button>
          <button className={`yummer-tab ${activeTab === 'cart' ? 'yummer-tab-active' : ''}`} onClick={() => { setActiveTab('cart'); setShowCart(true); }}>
            <ShoppingCart className="w-5 h-5" />
            <span className="yummer-tab-label">Carrinho</span>
          </button>
          <button className={`yummer-tab ${activeTab === 'phone' ? 'yummer-tab-active' : ''}`} onClick={() => { setActiveTab('phone'); window.location.href = `tel:${phoneHref(selectedBranch?.phone || tenant.phone)}`; }}>
            <Phone className="w-5 h-5" />
            <span className="yummer-tab-label">Telefone</span>
          </button>
        </div>
      </div>

      {/* ===== Cart drawer (slide from right with Motion) ===== */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowCart(false)}
          />
          <motion.div
            initial={reduce ? false : { x: '100%' }}
            animate={{ x: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="relative bg-surface-0 w-full max-w-md h-full overflow-auto shadow-2xl"
          >
            <div className="sticky top-0 bg-surface-0 border-b border-surface-3 p-4 flex justify-between items-center z-10">
              <h2 className="text-lg font-bold font-display text-ink-900">Carrinho</h2>
              <button onClick={() => setShowCart(false)} aria-label="Fechar carrinho" className="p-2 rounded-lg text-ink-500 hover:bg-surface-2 hover:text-ink-900 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {cart.length > 0 && cartSuggestions.length > 0 && (
                <CartSuggestions
                  suggestions={cartSuggestions}
                  onAdd={(item) => addToCart(item as MenuItem)}
                  onDismiss={dismissUpsellRule}
                />
              )}
              {cart.length === 0 ? (
                <EmptyState
                  icon={<ShoppingCart className="w-12 h-12" />}
                  title="Carrinho vazio"
                  description="Adiciona itens da ementa para começar."
                />
              ) : (
                cart.map((item) => (
                  <motion.div
                    key={item.lineId}
                    initial={reduce ? false : { opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="border-b border-surface-3 pb-3 last:border-0"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      {item.image ? (
                        <img src={item.image} alt="" loading="lazy" className="h-11 w-11 rounded-lg object-cover flex-shrink-0" />
                      ) : null}
                      <div className="min-w-0">
                        <h4 className="font-medium text-sm text-ink-900 truncate">{item.name}</h4>
                      <p className="text-sm text-ink-500">{formatPrice(Number(item.price))}</p>
                        {item.notes && <p className="text-xs text-ink-500 mt-0.5">{item.notes}</p>}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-[44px_32px_44px_minmax(64px,1fr)_44px] items-center gap-2">
                      <button onClick={() => updateQty(item.lineId, -1)} aria-label={`Reduzir quantidade de ${item.name}`} className="w-11 h-11 rounded-lg border border-surface-3 flex items-center justify-center text-ink-700 hover:bg-surface-2 transition-colors">
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium text-ink-900">{item.quantity}</span>
                      <button onClick={() => updateQty(item.lineId, 1)} aria-label={`Aumentar quantidade de ${item.name}`} className="w-11 h-11 rounded-lg bg-brand text-brand-contrast flex items-center justify-center hover:brightness-95 transition">
                        <Plus className="w-4 h-4" />
                      </button>
                    <span className="justify-self-end text-right text-sm font-semibold text-ink-900">{formatPrice(Number(item.price) * item.quantity)}</span>
                      <button onClick={() => removeFromCart(item.lineId)} aria-label={`Remover ${item.name}`} className="w-11 h-11 rounded-lg text-danger hover:bg-danger-soft transition-colors flex items-center justify-center">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div className="sticky bottom-0 bg-surface-0 border-t border-surface-3 p-4 space-y-3 safe-bottom">
                <div className="flex justify-between font-bold text-lg text-ink-900">
                  <span>Total</span>
                <span className="text-brand">{formatPrice(cartTotal)}</span>
                </div>
                <button
                  onClick={() => { setShowCart(false); setShowCheckout(true); }}
                  className="btn-gradient"
                >
                  Finalizar Encomenda
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* ===== Checkout modal ===== */}
      <Modal
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        title="Finalizar Encomenda"
        size="sm"
        footer={
          <button
            onClick={submitOrder}
            disabled={!customer.name || !customer.phone || submittingOrder}
            className="btn-gradient"
          >
            {submittingOrder ? 'A enviar...' : 'Enviar encomenda'}
          </button>
        }
      >
        <div className="space-y-3 mb-4">
          <div className="bg-surface-2 rounded-xl p-3.5 text-sm">
            {cart.map((i) => (
              <div key={i.lineId} className="flex justify-between py-1 text-ink-700">
                <span className="min-w-0">
                  {i.quantity}x {i.name}
                  {i.notes && <span className="block text-xs text-ink-500">{i.notes}</span>}
                </span>
                  <span className="font-medium text-ink-900">{formatPrice(Number(i.price) * i.quantity)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold pt-2 border-t border-surface-3 mt-2 text-ink-900">
              <span>Total</span>
              <span>{formatPrice(cartTotal)}</span>
            </div>
          </div>
          {selectedBranch && (
            <div className="rounded-xl border border-surface-3 bg-surface-0 p-3 text-sm">
              <p className="font-semibold text-ink-900">Loja para levantamento</p>
              <p className="mt-1 text-ink-700">{branchLabel(selectedBranch)}</p>
              <p className="mt-0.5 text-xs text-ink-500">{branchAddress(selectedBranch)}</p>
            </div>
          )}
          <Input
            type="text"
            placeholder="O seu nome"
            label="Nome *"
            value={customer.name}
            onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
          />
          <Input
            type="tel"
            placeholder="Número de telefone"
            label="Telefone *"
            value={customer.phone}
            onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
          />
          <Input
            type="email"
            placeholder="email@exemplo.com"
            label="Email (opcional)"
            value={customer.email}
            onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
          />
          <Textarea
            placeholder="Alergias, preferências, instruções..."
            label="Observações"
            value={customer.notes}
            onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
            rows={2}
          />
          {checkoutError && (
            <p className="rounded-xl bg-danger-soft p-3 text-sm font-medium text-danger" role="alert">{checkoutError}</p>
          )}
        </div>
      </Modal>

      {/* ===== Product detail bottom sheet ===== */}
      <Modal
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        title={selectedProduct?.name}
        size="sm"
        footer={
          <button
            onClick={() => {
              if (!selectedProduct) return;
              const notes = pendingRule ? buildCustomNotes(pendingRule, customSelections) : undefined;
              for (let n = 0; n < productQty; n++) addToCart(selectedProduct, notes);
              setSelectedProduct(null);
            }}
            className="btn-gradient"
          >
            Adicionar ao carrinho
          </button>
        }
      >
        {selectedProduct && (
          <div className="space-y-4">
            <div className="w-full h-48 rounded-xl overflow-hidden bg-gradient-to-br from-brand-50 to-surface-2 flex items-center justify-center">
              <img
                src={resolveDishImage(selectedProduct, categoryById.get(selectedProduct.category_id)?.name)}
                alt={selectedProduct.name}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(event) => { event.currentTarget.src = DEFAULT_DISH_IMAGE; }}
              />
            </div>
            <p className="text-sm text-ink-700">{selectedProduct.description}</p>
            {pendingRule && (
              <div className="space-y-3">
                {pendingRule.groups.map((group) => (
                  <div key={group.id}>
                    <span className="text-xs font-semibold text-ink-500 uppercase tracking-wide">
                      {group.label}
                      {group.required && <span className="text-danger ml-1">*</span>}
                    </span>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {group.options.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setCustomSelections((prev) => ({ ...prev, [group.id]: opt.id }))}
                          className={`px-3 py-2 min-h-[40px] rounded-lg text-sm font-medium transition ${
                            customSelections[group.id] === opt.id
                              ? 'bg-brand text-brand-contrast border-2 border-brand'
                              : 'bg-surface-2 text-ink-700 border-2 border-transparent hover:bg-surface-3'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-xs text-ink-500">Podes saltar opções não obrigatórias.</p>
              </div>
            )}
            <div className="flex items-center justify-between">
          <span className="yummer-dish-price text-lg">{formatPrice(Number(selectedProduct.price))}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setProductQty((q) => Math.max(1, q - 1))} className="w-8 h-8 rounded-lg border border-surface-3 flex items-center justify-center text-ink-700 hover:bg-surface-2 transition-colors">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium text-ink-900">{productQty}</span>
                <button onClick={() => setProductQty((q) => q + 1)} className="w-8 h-8 rounded-lg bg-brand text-brand-contrast flex items-center justify-center hover:brightness-95 transition">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ===== Order confirmation ===== */}
      <Modal open={orderSent} onClose={() => setOrderSent(false)} size="sm">
        <div className="text-center py-4">
          <motion.div
            initial={reduce ? false : { scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 15 }}
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-brand shadow-glow"
          >
            <Check className="w-8 h-8 text-brand-contrast" />
          </motion.div>
          <h2 className="text-xl font-bold font-display text-ink-900 mb-2 tracking-tight">Encomenda enviada</h2>
          <p className="text-ink-500 mb-6">
            {orderQueued
              ? 'O pedido ficou guardado e será enviado automaticamente quando a ligação voltar.'
              : 'Recebemos o teu pedido. Entraremos em contacto brevemente para confirmar.'}
          </p>
          <button onClick={() => setOrderSent(false)} className="btn-gradient">
            Fechar
          </button>
        </div>
      </Modal>

      {postAddSuggestions.length > 0 && !showCart && !showOrderWizard && (
        <div className="order-add-toast safe-bottom">
          <CartSuggestions
            suggestions={postAddSuggestions}
            onAdd={(item) => addToCart(item as MenuItem)}
            onDismiss={(ruleId) => {
              dismissUpsellRule(ruleId);
              setLastAddedItem(null);
            }}
            compact
          />
          <button
            type="button"
            className="order-add-toast-close"
            onClick={() => setLastAddedItem(null)}
            aria-label="Fechar sugestoes"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <OrderWizard
        open={showOrderWizard}
        onClose={() => setShowOrderWizard(false)}
        items={items as PublicMenuItem[]}
        categories={salesCategories}
        upsellRules={upsellRules}
        wizardSettings={orderWizardSettings}
        cart={cart}
        customer={customer}
        onCustomerChange={setCustomer}
        onAddToCart={(item) => addToCart(item as MenuItem)}
        onUpdateQty={updateQty}
        onRemoveFromCart={removeFromCart}
        onSubmit={submitOrder}
        submitting={submittingOrder}
        resolveImage={(item) => resolveDishImage(item as MenuItem, categoryById.get(item.category_id)?.name)}
        dismissedRuleIds={dismissedRuleIds}
        onDismissRule={dismissUpsellRule}
      />

      <RestaurantAssistant
        open={assistantOpen}
        onOpenChange={setAssistantOpen}
        tenantName={selectedBranch ? `${tenant.name} - ${selectedBranch.name}` : tenant.name}
        tenantPhone={selectedBranch?.phone || tenant.phone}
        branch={selectedBranch ? {
          name: selectedBranch.name,
          phone: selectedBranch.phone,
          address: branchAddress(selectedBranch),
          hours: selectedBranch.hours || tenant.hours,
        } : null}
        apiQuery={apiQuery}
        items={items}
        cart={cart}
        customer={customer}
        pageContext={{
          activeTab,
          searchQuery,
          checkoutOpen: showCheckout,
        }}
        onAction={applyAssistantAction}
      />

      <Modal open={showAbout} onClose={() => setShowAbout(false)} title={tenant.name}>
        <div className="space-y-4">
          {tenant.tagline && <p className="text-sm font-semibold text-brand">{tenant.tagline}</p>}
          {(restaurant?.description || tenant.tagline) && (
            <p className="text-sm leading-relaxed text-ink-500">{restaurant?.description || tenant.tagline}</p>
          )}
          <div className="space-y-2 pt-2">
            <a href={`tel:${phoneHref(tenant.phone)}`} className="flex items-center gap-2 text-sm text-ink-700 hover:text-brand">
              <Phone className="h-4 w-4 text-brand" /> {tenant.phone}
            </a>
            <p className="flex items-center gap-2 text-sm text-ink-700">
              <MapPin className="h-4 w-4 text-brand" /> {[tenant.address, tenant.city].filter(Boolean).join(', ')}
            </p>
            <p className="flex items-center gap-2 text-sm text-ink-700">
              <Clock className="h-4 w-4 text-brand" /> {tenant.hours}
            </p>
          </div>
        </div>
      </Modal>

      <DemoQuickAccess
        enabled={demoQuickAccess}
        slug={restaurant?.slug || tenant.slug}
        variant="floating"
      />
    </div>
  );
}
